import { getJwtString } from '@backoffice-frontend/authentication'
import {
  ENV,
  Environment,
  mockServerPort,
} from '@backoffice-frontend/environment'
import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import cube from '@cubejs-client/core'
import { CubeProvider as Provider } from '@cubejs-client/react'
import { type ReactNode, useMemo } from 'react'
import { InstrumentedHttpTransport } from './InstrumentedHttpTransport'
import {
  type CubeRetryConfig,
  DEFAULT_CUBE_RETRY_CONFIG,
  RetryingHttpTransport,
} from './RetryingHttpTransport'

// Local Cube is served by the mock server by default, so selecting "local"
// loads offline fixtures — consistent with how GraphQL is mock-served (see
// GraphQLStages in endpoints.ts). To point at a locally-running Cube repo
// (e.g. http://localhost:4000) instead, set VITE_CUBE_ENDPOINT_OVERRIDE — no
// rebuild needed, same escape hatch as VITE_GRAPHQL_ENDPOINT_OVERRIDE.
const localCubeBaseUrl =
  __VITE_CUBE_ENDPOINT_OVERRIDE__ || `http://localhost:${mockServerPort}`

// API endpoints
const backofficeDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/cubejs-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://backoffice-data-api.insights.dev.moia-group.io/cubejs-api/v1',
  [Environment.integration]:
    'https://backoffice-data-api.insights.int.moia-group.io/cubejs-api/v1',
  [Environment.production]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
  [Environment['production.us-east-1']]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
}
const backofficeMultitenantDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/multi-tenant-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://multi-tenant-cube-data-api.insights.dev.moia-group.io/multi-tenant-api/v1',
  [Environment.integration]:
    'https://multi-tenant-cube-data-api.insights.int.moia-group.io/multi-tenant-api/v1',
  [Environment.production]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
  [Environment['production.us-east-1']]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
}
const dapDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/multi-tenant-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://cube-api.dev.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment.integration]:
    'https://cube-api.int.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment.production]:
    'https://cube-api.prd.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment['production.us-east-1']]:
    'https://cube-api.prd.us-east-1.api.moia-group.io/multi-tenant-api/v1',
}

const getToken = async () => {
  const token = await getJwtString()
  return token ?? ''
}

type CubeApiSet = {
  regular: ReturnType<typeof cube>
  multitenant: ReturnType<typeof cube>
  dap: ReturnType<typeof cube>
}

// cube-api can't read POST bodies, so large queries (sent as POST past 2000 URL
// chars) need the tenant named in headers. Drop once cube-js/cube#11987 ships.
const TENANT_HEADER = 'x-cube-tenant'
const TENANT_TYPE_HEADER = 'x-cube-tenant-type'

export type DapTenant = {
  tenant: string
  tenantType:
    | 'operator_tenant'
    | 'ridepooling_service_tenant'
    | 'customer_tenant'
}

// `retryConfig` undefined → plain instrumented transport (current behavior).
// Defined → retrying transport that transparently retries transient failures.
const createCubeApis = (
  retryConfig?: CubeRetryConfig,
  dapTenant?: DapTenant,
): CubeApiSet => {
  const makeTransport = (apiUrl: string, headers?: Record<string, string>) =>
    retryConfig
      ? new RetryingHttpTransport({ apiUrl, headers }, retryConfig)
      : new InstrumentedHttpTransport({ apiUrl, headers })

  const dapHeaders = dapTenant
    ? {
        [TENANT_HEADER]: dapTenant.tenant,
        [TENANT_TYPE_HEADER]: dapTenant.tenantType,
      }
    : undefined

  return {
    regular: cube(getToken, {
      apiUrl: backofficeDataApiEndpoints[ENV],
      transport: makeTransport(backofficeDataApiEndpoints[ENV]),
    }),
    multitenant: cube(getToken, {
      apiUrl: backofficeMultitenantDataApiEndpoints[ENV],
      transport: makeTransport(backofficeMultitenantDataApiEndpoints[ENV]),
    }),
    dap: cube(getToken, {
      apiUrl: dapDataApiEndpoints[ENV],
      transport: makeTransport(dapDataApiEndpoints[ENV], dapHeaders),
    }),
  }
}

export const __test__createCubeApis = createCubeApis

// Default (non-retrying) APIs are module-level singletons, unchanged.
const defaultCubeApis = createCubeApis()

// Simple provider that allows switching between APIs
export const CubeProvider = ({
  children,
  useMultitenantCubeApi = false,
  useDapCubeApi = false,
  dapTenant,
  retry = false,
}: {
  children: ReactNode
  useMultitenantCubeApi?: boolean
  // Opts a single view into the DAP Cube endpoint for all users, without
  // waiting for the DAP_CUBE_ENDPOINT_V2 toggle to be enabled globally. Use
  // this to migrate views to DAP one at a time; the toggle stays the escape
  // hatch for migrating everything else at once.
  useDapCubeApi?: boolean
  /** Sent as `x-cube-tenant(-type)`; must match the tenant of the view's cubes. */
  dapTenant?: DapTenant
  /**
   * Transparently retry transient Cube failures (5xx / network / timeout) with
   * a backoff schedule. `true` uses the default (immediate, +2s, +5s); pass a
   * {@link CubeRetryConfig} to customize. Opt-in — consumers that omit it keep
   * the current no-retry behavior.
   */
  retry?: boolean | CubeRetryConfig
}) => {
  const isDapEndpointToggleEnabled = useIsFeatureToggleEnabled(
    Features.DAP_CUBE_ENDPOINT_V2,
  )

  const retryConfig = retry
    ? retry === true
      ? DEFAULT_CUBE_RETRY_CONFIG
      : retry
    : undefined

  // Rebuild when the tenant changes (region switch): it's baked into the headers.
  const tenant = dapTenant?.tenant
  const tenantType = dapTenant?.tenantType
  const apis = useMemo(
    () =>
      retryConfig || (tenant && tenantType)
        ? createCubeApis(
            retryConfig,
            tenant && tenantType ? { tenant, tenantType } : undefined,
          )
        : defaultCubeApis,
    [retryConfig, tenant, tenantType],
  )

  const selectedApi =
    useDapCubeApi || isDapEndpointToggleEnabled
      ? apis.dap
      : useMultitenantCubeApi
        ? apis.multitenant
        : apis.regular

  return <Provider cubeApi={selectedApi}>{children}</Provider>
}
